<!-- Differs from standard modal in that history state is unaffected here -->
<template>
    <transition @after-leave="closeComplete">
        <div class="newModal__mask"
             :class="{ 'modal__mask': oldModalStyles }"
             v-show="visible"
             tabindex="0"
             :aria-label="closeBtnLabel"
             @click="closeMask"
             @keydown.enter.self.prevent="closeMask"
             @keydown.space.self.prevent="closeMask"
             >
            <div class="newModal__container"
                 :class="{ 'modal__container': oldModalStyles }"
                 role="dialog"
                 aria-modal="true"
                 :aria-label="ariaLabel || dialogLabel"
                 >
                <button type="button"
                        class="newModal__btn--close"
                        :class="{ 'modal__btn--close': oldModalStyles }"
                        id="modal--description"
                        @keydown.enter.stop.prevent="close"
                        @keydown.space.stop.prevent="close"
                        @click.stop="close"
                        :aria-label="closeBtnLabel"
                    >
                    <svg-icon name="close" aria-hidden="true"></svg-icon>
                </button>

                <header class="newModal__header" :class="{ 'modal__header': oldModalStyles }" @click.stop=""><slot name="header"></slot></header>
                <section class="newModal__body" :class="{ 'newModal__body--iframe': iframe, 'modal__body': oldModalStyles, 'modal__body--iframe': (iframe && oldModalStyles) }" @click.stop="">
                    <slot></slot>
                    <iframe v-if="iframe" :src="iframe" height="100%" width="100%" class="newModal__iframe" :class="{ 'modal__iframe': oldModalStyles }"></iframe>
                </section>
                <footer class="newModal__footer" :class="{ 'modal__footer': oldModalStyles }" @click.stop=""><slot name="footer"></slot></footer>
            </div>
        </div>
    </transition>
</template>

<script>
    export default {
        name: 'newModal',
        data: function ()  {
            return {
                interactedWith: false
            }
        },
        props: {
            dialogLabel: {
                default: 'A modal is open and overlays the page. Press escape to close the modal.',
                type: String,
            },
            closeBtnLabel: {
                default: 'Close',
                type: String,
            },
            ariaLabel: {
                default: '',
                type: String,
            },
            visible: {
                default: false,
                type: Boolean,
            },
            trapFocus: {
                default: false,
                type: Boolean,
            },
            onClose: {
                default: false,
                type: [
                    Boolean,
                    Function,
                ],
            },
            onCloseComplete: {
                default: false,
                type: [
                    Boolean,
                    Function,
                ],
            },
            refocusElement: {
                default: '',
                type: String
            },
            iframe: {
                default: '',
                type: String,
            },
            oldModalStyles: {
                default: false,
                type: Boolean,
            }
        },
        created() {
            this.addEvents();
        },
        watch: {
            visible() {
                if(this.trapFocus){
                const e = this.$el.querySelector('.newModal__container');
                const focusable = e.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
                const firstFocusable = focusable[0];
                const lastFocusable = focusable[focusable.length - 1];
                

                function firstHandler(e) {
                    if (e.shiftKey && e.key === 'Tab') {
                        e.preventDefault();
                        lastFocusable.focus();
                    }
                }

                function lastHandler(e) {
                    if (e.key === 'Tab' && !e.shiftKey) {
                        e.preventDefault();
                        firstFocusable.focus();
                    }
                }

                function removeEventListener() {
                    firstFocusable.removeEventListener('keydown', firstHandler);
                    lastFocusable.removeEventListener('keydown', lastHandler);
                }

                if (this.visible == true) {
                    if (this.$data.interactedWith == true) {
                        removeEventListener();
                    }

                    this.$data.interactedWith = true;
                    setTimeout(function(){
                        firstFocusable.focus();
                        // delay for VO focus
                    }, 500);
                    
                    firstFocusable.addEventListener('keydown', firstHandler);
                    lastFocusable.addEventListener('keydown', lastHandler);
                }
                }
            },
        },
        methods: {
            addEvents() {
                // Escape key close
                document.addEventListener('keydown', (e) => {
                    if (this.visible && e.keyCode === 27) {
                        this.close();
                    }
                }, false);

                // Modal close. The listener is global, so dismissing any other modal on the
                // page clears the hash and lands here too. Closing a modal that was never open
                // fires its consumer's on-close, which is how a dismissed modal ends up stealing
                // focus from whatever the open one restored it to.
                window.addEventListener('hashchange', () => {
                    if (!window.location.hash && this.visible) {
                        this.close();
                    }
                }, false);
            },
            close() {
                this.removeHash();

                if (typeof this.onClose === 'function') {
                    this.onClose();
                } else {
                    this.visible = false;
                }
                if(this.refocusElement){
                    var el = this.refocusElement;
                    setTimeout(function(){
                        // Set Timeout for VO focus
                        document.querySelector(el).focus();
                    }, 500);
                }
            },
            closeComplete() {
                if (typeof this.onCloseComplete === 'function') {
                    this.onCloseComplete();
                }
            },
            closeMask(e) {
                if (!e.target.closest('.newModal__container')) {
                    this.close();
                }
            },
            open() {
                this.visible = true;
            },
            removeHash() {
                let scrollV = document.body.scrollTop,
                    scrollH = document.body.scrollLeft;
                if ('pushState' in history) {
                    setTimeout(() => {
                        history.replaceState('', document.title, window.location.pathname + window.location.search);
                    }, 10);
                } else {

                    location.hash = '';
                    document.body.scrollTop = scrollV;
                    document.body.scrollLeft = scrollH;
                }
            },
        },
    };
</script>

<style rel="stylesheet/scss" lang="scss" type="text/scss">
    /* stylelint-disable selector-class-pattern */
    @use 'sass:math';

    @import '~@sweetwater/sass-resources/sass';

    @keyframes rotatePlaceholder {
        0% {
            transform: rotate(-45deg);
        }

        5% {
            transform: rotate(-45deg);
        }

        12% {
            transform: rotate(-405deg);
        }

        100% {
            transform: rotate(-405deg);
        }
    }

    @keyframes animate-success-tip {
        0% {
            left: 1px;
            top: 19px;
            width: 0;
        }

        54% {
            left: 1px;
            top: 19px;
            width: 0;
        }

        70% {
            left: -8px;
            top: 37px;
            width: 50px;
        }

        84% {
            left: 21px;
            top: 48px;
            width: 17px;
        }

        100% {
            left: 14px;
            top: 45px;
            width: 25px;
        }
    }

    @keyframes animate-success-long {
        0% {
            right: 46px;
            top: 54px;
            width: 0;
        }

        65% {
            right: 46px;
            top: 54px;
            width: 0;
        }

        84% {
            right: 0;
            top: 35px;
            width: 55px;
        }

        100% {
            right: 8px;
            top: 38px;
            width: 47px;
        }
    }

    body.overflow-hidden {
        overflow: hidden;
    }

    .newModal__mask {
        background-color: rgba(0, 0, 0, 0.6);
        box-sizing: border-box;
        height: 100vh;
        left: 0;
        padding: 0 16px;
        position: fixed;
        top: 0;
        width: 100vw;
        z-index: 9998;

        .flexbox & {
            align-items: center;
            display: flex;
            flex-direction: column;
            justify-content: center;
        }
    }

    .newModal__container {
        background-color: #fff;
        border-radius: 2px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
        height: auto;
        margin: 0 auto;
        max-height: 90%;
        max-width: 1000px;
        position: relative;
        width: auto;

        .flexbox & {
            display: flex;
            flex-direction: column;
        }
    }

    @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
        .newModal__container {
            overflow: auto;
            width: 100%;
        }
    }

    .newModal__header,
    .newModal__footer {
        flex: none;
        height: auto;
        position: relative;
        z-index: 1;

        &:empty {
            display: none;
        }
    }

    /* Header */
    .newModal__header {
        padding: 20px 56px 10px;
        text-align: center;

        @include media('<=xsmall') {
            padding: 20px 43px 10px 40px;
        }
    }

    .newModal__header h2,
    .newModal__header h3 {
        color: inherit;
        font: normal 600 36px $font-stack-condensed;
        margin: 0;
    }

    .newModal__rev-header .newModal__header {
        background: $dark-grey;
        border: solid #fff;
        border-width: 1px 1px 0;
        color: #fff;
    }

    /* Body */
    .newModal__body {
        /* stylelint-disable-next-line property-no-unknown */
        overflow-scrolling: touch;
        overflow-x: hidden;
        overflow-y: auto;
        padding: 15px 25px 25px;
        @include media('>=small') {
            padding: 15px 25px;
        }
        @include media('<=xsmall') {
            padding: 15px 5px 25px;
        }
    }

    .newModal__body:not(.modal__body) {
        background: #eeebe7;
    }

    .newModal__body p {
        line-height: 1.4;
        margin: 0 0 12px;
    }

    .newModal__body strong {
        font-weight: 600;
    }

    /* Iframe */
    .newModal__body--iframe {
        overflow: hidden;
        padding: 0 16px;

        @include media('>=small') {
            padding: 0 25px;
        }

        .flexbox & {
            display: flex;
            flex-direction: column;
        }
    }

    .newModal__iframe {
        border: 0;
        height: 1000px;
        max-height: 100%;
        max-width: 100%;
        width: 1000px;
    }

    /* Footer */
    .newModal__footer {
        padding: 10px 15px 20px;

        &::before {
            background: linear-gradient(to top, #fff, rgba(255, 255, 255, 0));
            content: '';
            height: 15px;
            left: 0;
            position: absolute;
            top: -15px;
            width: 100%;
        }
    }

    /* Buttons */
    .newModal__btn {
        min-width: 120px;

        + .newModal__btn {
            margin-left: 10px;
        }
    }

    /* Close button */
    .newModal__btn--close {
        background: rgba(#fff, 0.5);
        border: 0;

        /* trying to compensate for a strange focus outline issue */
        border-radius: 500px 501px!important;
        color: rgba($dark-grey, 0.6);
        cursor: pointer;
        height: 40px;
        position: absolute;
        right: 5px;
        text-align: left;
        text-indent: -9999px;
        top: 5px;
        width: 40px;
        z-index: 2;

        &:hover {
            background: rgba($dark-grey, 0.1);
        }

        .sr-only {
            @include visually-hidden;
        }
    }

    .newModal__rev-header .newModal__btn--close {
        background: none;
        color: #fff;

        &:hover {
            background: rgba(255, 255, 255, 0.1);
        }
    }

    .newModal__btn--close svg,
    .product .newModal__btn--close svg {
        $modal-close-size: 40%;
        fill: currentColor;
        height: $modal-close-size;
        left: math.div((100 - $modal-close-size), 2);
        position: absolute;
        top: math.div((100 - $modal-close-size), 2);
        width: $modal-close-size;
    }

    /* Show/hide transitions */
    .fade-enter-active,
    .fade-leave-active {
        opacity: 0;
    }

    .fade-enter-active .newModal__container,
    .fade-leave-active .newModal__container {
        transform: scale(0.9);
    }

    /* Success */
    .form__icon {
        border: 4px solid #555;
        border-radius: 50%;
        box-sizing: content-box;
        height: 80px;
        margin: 20px auto;
        padding: 0;
        position: relative;
        width: 80px;
    }

    .form__icon.form__success {
        border-color: #a5dc86;

        &::before,
        &::after {
            background: #fff;
            border-radius: 50%;
            content: '';
            height: 120px;
            position: absolute;
            transform: rotate(45deg);
            width: 60px;
        }

        &::before {
            border-radius: 120px 0 0 120px;
            left: -33px;
            top: -7px;
            transform: rotate(-45deg);
            transform-origin: 60px 60px;
        }

        &::after {
            border-radius: 0 120px 120px 0;
            left: 30px;
            top: -11px;
            transform: rotate(-45deg);
            transform-origin: 0 60px;
        }

        .form__icon-placeholder {
            border: 4px solid rgba(165, 220, 134, 0.2);
            border-radius: 50%;
            box-sizing: content-box;
            height: 80px;
            left: -4px;
            position: absolute;
            top: -4px;
            width: 80px;
            z-index: 2;
        }

        .form__icon-fix {
            background-color: #fff;
            height: 90px;
            left: 28px;
            position: absolute;
            top: 8px;
            transform: rotate(-45deg);
            width: 5px;
            z-index: 1;
        }

        .form__icon-line {
            background-color: #a5dc86;
            border-radius: 2px;
            display: block;
            height: 5px;
            position: absolute;
            z-index: 2;
        }

        .form__icon-line.form__icon-tip {
            left: 14px;
            top: 46px;
            transform: rotate(45deg);
            width: 25px;
        }

        .form__icon-line.form__icon-long {
            right: 8px;
            top: 38px;
            transform: rotate(-45deg);
            width: 47px;
        }
    }

    .animate-success-tip {
        animation: animate-success-tip 0.75s;
    }

    .animate-success-long {
        animation: animate-success-long 0.75s;
    }

    .form__icon.form__success.animate::after {
        animation: rotatePlaceholder 4.25s ease-in;
    }

    .form-label {
        display: block;
        margin-bottom: 1em;
    }

    .form-label > .form-control {
        margin-top: 0.5em;
    }

    .form-control {
        border: 1px solid #ddd;
        display: block;
        line-height: 1.5;
        padding: 0.5em 1em;
        width: 100%;
    }
    
</style>
